import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { eur, eurShort, pct } from "@/lib/format";
import { Card } from "@/components/ui/card";
import DeleteDemoData from "@/components/DeleteDemoData";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { COLORS, CATEGORY_COLORS, axisProps, ChartTooltip } from "@/components/chartUtils";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  AreaChart, Area, PieChart, Pie, Cell, LineChart, ReferenceLine,
} from "recharts";
import {
  Wallet, TrendUp, TrendDown, ArrowsLeftRight, Lightbulb, ArrowUp, ArrowDown, Repeat,
} from "@phosphor-icons/react";
import { CategoryIcon, categoryIndex, lookupCategory, colorMap } from "@/lib/categoryIcons";
import { useIsPhone } from "@/lib/useMediaQuery";
import { toast } from "sonner";
import { useAccountScope } from "@/context/AccountScopeContext";
import { withAccountScope } from "@/lib/accountScope";
import {
  DASHBOARD_PERIOD_OPTIONS,
  loadDashboardPeriod,
  saveDashboardPeriod,
} from "@/lib/dashboardPeriod";

const KpiCard = ({ label, value, sub, accent, icon: Icon, testid }) => (
  <Card
    data-testid={testid}
    className="bg-card border-border p-4 sm:p-5 rounded-lg h-full"
  >
    <div className="flex items-start justify-between gap-2">
      <p className="text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.15em] text-muted-foreground">{label}</p>
      {Icon && <Icon size={18} className={`shrink-0 ${accent || "text-muted-foreground"}`} />}
    </div>
    <p className={`font-heading font-extrabold text-xl sm:text-2xl lg:text-3xl tracking-tight mt-2 sm:mt-3 tabular-nums ${accent || ""}`}>
      {value}
    </p>
    {sub && <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 sm:mt-1.5">{sub}</p>}
  </Card>
);

export default function Dashboard() {
  const phone = useIsPhone();
  const { scope, selectedAccount, ready: accountsReady } = useAccountScope();
  const [period, setPeriod] = useState(loadDashboardPeriod);
  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [insights, setInsights] = useState([]);
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    // Wait for the account list, so a saved scope is checked before the first
    // request instead of loading every account and then reloading one.
    if (!accountsReady) return;
    setLoading(true);
    try {
      const response = await api.get(`/overview?${withAccountScope({ period }, scope)}`);
      setData(response.data.dashboard);
      setForecast(response.data.forecast);
      setInsights(response.data.insights);
      setCategories(response.data.categories);
    } catch {
      toast.error("Could not load the dashboard");
    } finally {
      setLoading(false);
    }
  }, [period, scope, accountsReady]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  // Slices are coloured by the colour the user picked for the category, so the
  // donut, the legend and the category page all agree; the positional palette
  // stays as the fallback for a name with no category behind it (an old
  // transaction whose category was since deleted, say).
  const catIndex = categoryIndex(categories);
  const expenseColors = colorMap(categories.expense);
  const projected12 = forecast?.scenarios?.expected?.projection?.[11]?.balance;

  // combine historical + projected balance for the forecast preview chart
  const balanceSeries = (() => {
    if (!forecast) return [];
    const hist = forecast.historical.map((h) => ({ label: h.label, historical: h.balance }));
    if (hist.length) hist[hist.length - 1].projected = forecast.historical[hist.length - 1].balance;
    const proj = forecast.scenarios.expected.projection.slice(0, 12).map((p) => ({ label: p.label, projected: p.balance }));
    return [...hist, ...proj];
  })();
  const boundaryLabel = forecast?.historical?.length ? forecast.historical[forecast.historical.length - 1].label : null;

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tighter">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Your income, expenses and cash-flow overview.</p>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Select
            value={period}
            onValueChange={(value) => {
              setPeriod(value);
              saveDashboardPeriod(value);
            }}
          >
            <SelectTrigger className="w-full sm:w-[180px] bg-card border-border" data-testid="period-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {DASHBOARD_PERIOD_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value} data-testid={`period-${p.value}`}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DeleteDemoData onDeleted={load} variant="banner" />

      {loading || !s ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 sm:h-32 bg-card" />)}
        </div>
      ) : (
        <>
          {/* Hero KPIs */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <KpiCard testid="kpi-balance" label="Current Balance" value={eur(s.current_balance)} icon={Wallet}
                     sub={selectedAccount ? selectedAccount.name : "Across all accounts"} />
            <KpiCard testid="kpi-net" label="Net Cash Flow" value={eur(s.net_cash_flow)}
                     accent={s.net_cash_flow >= 0 ? "text-income" : "text-expense"}
                     icon={ArrowsLeftRight} sub={`Savings rate ${pct(s.savings_rate, 0)}`} />
            <KpiCard testid="kpi-income" label="Total Income" value={eur(s.total_income)} accent="text-income"
                     icon={TrendUp} sub={`Avg ${eur(s.avg_monthly_income)}/mo`} />
            <KpiCard testid="kpi-expenses" label="Total Expenses" value={eur(s.total_expenses)} accent="text-expense"
                     icon={TrendDown} sub={`Avg ${eur(s.avg_monthly_expenses)}/mo`} />
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
            <KpiCard testid="kpi-avg-income" label="Avg Monthly Income" value={eur(s.avg_monthly_income)} />
            <KpiCard testid="kpi-avg-expenses" label="Avg Monthly Expenses" value={eur(s.avg_monthly_expenses)} />
            <KpiCard testid="kpi-avg-cashflow" label="Avg Monthly Cash Flow" value={eur(s.avg_monthly_cash_flow)}
                     accent={s.avg_monthly_cash_flow >= 0 ? "text-income" : "text-expense"} />
            <KpiCard testid="kpi-projected" label="Projected Balance · 12mo"
                     value={projected12 != null ? eur(projected12) : "—"} accent="text-forecast" icon={ChartLineUpMini}
                     sub="Projection · not guaranteed" />
          </div>

          {/* Charts row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg lg:col-span-2" data-testid="chart-cashflow">
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="font-heading font-bold tracking-tight">Monthly Cash Flow</h3>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-income" />Income</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-expense" />Expense</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-white" />Net</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={phone ? 210 : 280}>
                <ComposedChart data={data.monthly_series}>
                  <CartesianGrid vertical={false} stroke={COLORS.grid} />
                  <XAxis dataKey="label" {...axisProps} interval={phone ? "preserveStartEnd" : 0} minTickGap={phone ? 24 : 5} />
                  <YAxis {...axisProps} tickFormatter={eurShort} width={phone ? 38 : 48} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                  <Bar dataKey="income" name="Income" fill={COLORS.income} radius={[2, 2, 0, 0]} maxBarSize={26} />
                  <Bar dataKey="expense" name="Expense" fill={COLORS.expense} radius={[2, 2, 0, 0]} maxBarSize={26} />
                  <Line dataKey="net" name="Net" stroke={COLORS.balance} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Card>

            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg" data-testid="chart-category">
              <h3 className="font-heading font-bold tracking-tight mb-4">Spending by Category</h3>
              {data.expense_by_category.length ? (
                <>
                  <ResponsiveContainer width="100%" height={phone ? 170 : 200}>
                    <PieChart>
                      <Pie data={data.expense_by_category} dataKey="amount" nameKey="category"
                           innerRadius={55} outerRadius={85} paddingAngle={2} stroke="none">
                        {data.expense_by_category.map((c, i) => (
                          <Cell key={i} fill={expenseColors[c.category] || CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5 mt-2 max-h-[110px] overflow-auto">
                    {data.expense_by_category.slice(0, 6).map((c, i) => {
                      const color = expenseColors[c.category] || CATEGORY_COLORS[i % CATEGORY_COLORS.length];
                      return (
                      <div key={c.category} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-2">
                          <CategoryIcon
                            category={lookupCategory(catIndex, "expense", c.category)}
                            size={14}
                            color={color}
                          />
                          {c.category}
                        </span>
                        <span className="tabular-nums text-muted-foreground">{pct(c.percent, 0)}</span>
                      </div>
                      );
                    })}
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground py-10 text-center">No expenses in this period.</p>}
            </Card>
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg lg:col-span-2" data-testid="chart-balance-forecast">
              <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="font-heading font-bold tracking-tight">Balance · Historical &amp; Projected</h3>
                <span className="text-xs text-muted-foreground">Dashed = projection</span>
              </div>
              <ResponsiveContainer width="100%" height={phone ? 200 : 260}>
                <AreaChart data={balanceSeries}>
                  <defs>
                    <linearGradient id="histFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.balance} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={COLORS.balance} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="projFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.forecast} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={COLORS.forecast} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke={COLORS.grid} />
                  <XAxis dataKey="label" {...axisProps} interval={phone ? "preserveStartEnd" : 0} minTickGap={phone ? 24 : 5} />
                  <YAxis {...axisProps} tickFormatter={eurShort} width={phone ? 38 : 48} />
                  <Tooltip content={<ChartTooltip />} />
                  {boundaryLabel && <ReferenceLine x={boundaryLabel} stroke={COLORS.axis} strokeDasharray="3 3"
                                      label={{ value: "Now", fill: COLORS.axis, fontSize: 10, position: "top" }} />}
                  <Area dataKey="historical" name="Historical" stroke={COLORS.balance} strokeWidth={2}
                        fill="url(#histFill)" connectNulls dot={false} />
                  <Area dataKey="projected" name="Projected" stroke={COLORS.forecast} strokeWidth={2}
                        strokeDasharray="5 4" fill="url(#projFill)" connectNulls dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg" data-testid="chart-savings-rate">
              <h3 className="font-heading font-bold tracking-tight mb-4">Monthly Savings Rate</h3>
              <ResponsiveContainer width="100%" height={phone ? 170 : 200}>
                <LineChart data={data.monthly_series.map((m) => ({
                  label: m.label,
                  rate: m.income > 0 ? Number(((m.net / m.income) * 100).toFixed(1)) : 0,
                }))}>
                  <CartesianGrid vertical={false} stroke={COLORS.grid} />
                  <XAxis dataKey="label" {...axisProps} interval={phone ? "preserveStartEnd" : 0} minTickGap={phone ? 24 : 5} />
                  <YAxis {...axisProps} width={phone ? 32 : 38} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<ChartTooltip valueFormatter={(v) => `${v}%`} />} />
                  <ReferenceLine y={0} stroke={COLORS.grid} />
                  <Line dataKey="rate" name="Savings rate" stroke={COLORS.income} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="border border-border rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Repeat size={11} /> Recurring In</p>
                  <p className="tabular-nums text-income font-semibold mt-1">{eur(data.recurring.recurring_monthly_income)}</p>
                </div>
                <div className="border border-border rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Repeat size={11} /> Recurring Out</p>
                  <p className="tabular-nums text-expense font-semibold mt-1">{eur(data.recurring.recurring_monthly_expense)}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Insights */}
          <Card className="bg-card border-border p-4 sm:p-5 rounded-lg" data-testid="insights-panel">
            <h3 className="font-heading font-bold tracking-tight mb-4 flex items-center gap-2">
              <Lightbulb size={18} className="text-income" /> Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {insights.length ? insights.map((ins, i) => (
                <div key={i} data-testid={`insight-${i}`} className="flex items-start gap-3 border border-border rounded-md p-3.5">
                  <span className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                    ins.tone === "positive" ? "bg-income/15 text-income" :
                    ins.tone === "negative" ? "bg-expense/15 text-expense" : "bg-secondary text-muted-foreground"
                  }`}>
                    {ins.tone === "negative" ? <ArrowDown size={13} weight="bold" /> : <ArrowUp size={13} weight="bold" />}
                  </span>
                  <p className="text-sm leading-relaxed">{ins.text}</p>
                </div>
              )) : <p className="text-sm text-muted-foreground">Add more transactions to unlock insights.</p>}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function ChartLineUpMini(props) {
  return <TrendUp {...props} />;
}
